import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { fetchPagesWithQuery } from "features/common/paginationUtils";
import apiCore from "services/apiCore";

type Division = {
    id: string;
    name: string;
    selfUri: string;
};

type DivisionsState = {
    status: "idle" | "pending" | "finished" | "failed";
    data: Division[];
    selectedDivision: Division | null;
    error?: string;
};

export const UNASSIGNED_DIVISION: Division = {
    id: "unassigned",
    name: "Unassigned",
    selfUri: "",
};

const initialState: DivisionsState = {
    status: "idle",
    data: [],
    selectedDivision: null,
};

export const loadDivisions = createAsyncThunk(
    "divisions/loadDivisions",
    async (permission: string) => {
        const fetchDivisionsWithPermission = async (requestData: {
            permission: string;
            pageNumber: number;
            pageSize: number;
        }) => {
            return apiCore.v2.get(
                "/authorization/divisionspermitted/paged/me",
                {
                    permission: requestData.permission,
                    pageNumber: requestData.pageNumber,
                    pageSize: requestData.pageSize,
                }
            );
        };

        const divisions = (await fetchPagesWithQuery(
            fetchDivisionsWithPermission,
            { permission },
            1000,
            1
        )) as Division[];

        return [UNASSIGNED_DIVISION, ...divisions];
    }
);

export const slice = createSlice({
    name: "divisions",
    initialState,
    reducers: {
        setSelectedDivision: (
            state,
            { payload }: { payload: Division | null }
        ) => {
            state.selectedDivision = payload;
        },
        clearDivisions: (state) => {
            state.data = [];
            state.selectedDivision = null;
            state.status = "idle";
            state.error = undefined;
        },
        resetSelectedDivision: (state) => {
            state.selectedDivision = null;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(loadDivisions.pending, (state) => {
                state.status = "pending";
                state.error = undefined;
            })
            .addCase(loadDivisions.fulfilled, (state, { payload }) => {
                state.status = "finished";
                state.data = payload;
            })
            .addCase(loadDivisions.rejected, (state, { error }) => {
                state.status = "failed";
                state.error = error.message;
                // Provide fallback "Unassigned" option when loading fails
                state.data = [UNASSIGNED_DIVISION];
            });
    },
});

export const { setSelectedDivision, clearDivisions, resetSelectedDivision } =
    slice.actions;

export const selectDivisions = (state: RootState) => state.divisions.data;
export const selectDivisionsStatus = (state: RootState) =>
    state.divisions.status;
export const selectSelectedDivision = (state: RootState) =>
    state.divisions.selectedDivision;

// Selector to find a division by ID
export const selectDivisionById = (state: RootState, divisionId: string) =>
    state.divisions.data.find((division) => division.id === divisionId);

// Selector to check if divisions are loaded
export const selectAreDivisionsLoaded = (state: RootState) =>
    state.divisions.status === "finished" ||
    state.divisions.status === "failed";

export default slice.reducer;
